// frontend/src/components/digital_twin/AssetHealthPanel.js
// --- UPDATE (Digital Twin MOW+ v1) ---------------------------------------------------------
//   FIXES
//   • failure_probability sent as a percentage (e.g. 62) showed as 6200 %; 0–1 and 0–100 accepted.
//   • A risk of exactly 0 % hid the risk bar; "N/A" RUL / confidence now "—" (no invented values).
//   • status "good"/"normal" both shown as healthy; unknown status no longer prints "UNKNOWN" as
//     if it were a diagnosis — "NOT SCORED".
//   FEATURES (optional props)
//   • `riskScore` — the FlowSense explainable risk score for this asset: score, band, drivers (why)
//     and recommended actions.
//   • ML model contributing factors when the model returns them (top_factors /
//     contributing_factors / feature_importance), model version and scoring time.
//   • `liveStatus` — the live sensor status next to the ML status, so a disagreement is visible.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useMemo } from 'react';
import {
  Box, Text, VStack, HStack, Badge, Flex, Icon, 
  Progress, useColorModeValue, SimpleGrid, Divider, Alert, AlertIcon
} from '@chakra-ui/react';
import { 
  FaBrain, FaClock, FaBullseye, FaExclamationTriangle, 
  FaCheckCircle, FaHeartbeat 
} from 'react-icons/fa';
import { motion } from 'framer-motion';
// --- UPDATE
import { Tooltip, Wrap, WrapItem } from '@chakra-ui/react';
import { FaListUl, FaLightbulb } from 'react-icons/fa';
import { failureProbability } from './twinIntel';

/** Model explanation in whatever shape the ML service returns it. */
const factorsOf = (h) => {
  const raw = h?.top_factors ?? h?.contributing_factors ?? h?.feature_importance ?? h?.explanations ?? null;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((f) => (typeof f === 'string' ? { label: f } : { label: f.feature ?? f.name ?? f.label ?? String(f), value: f.importance ?? f.weight ?? f.value ?? null })).slice(0, 6);
  if (typeof raw === 'object') return Object.entries(raw).map(([k, v]) => ({ label: k, value: v })).sort((a, b) => Math.abs(Number(b.value) || 0) - Math.abs(Number(a.value) || 0)).slice(0, 6);
  return [];
};
// --- END UPDATE

const MotionBox = motion(Box);

// ==================== ANIMATIONS ====================
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const AssetHealthPanel = ({ assetHealth, riskScore = null, liveStatus = null }) => { // --- UPDATE: + riskScore, liveStatus
  // ==================== ALL HOOKS AT TOP LEVEL ====================
  const cardBg = useColorModeValue('white', 'gray.800');
  const innerBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'white');
  const progressBg = useColorModeValue('gray.100', 'gray.700'); // FIX: Hoisted from inside the conditional render
  
  const alertBgCritical = useColorModeValue('red.50', 'rgba(229, 62, 62, 0.15)');
  const alertBorderCritical = useColorModeValue('red.200', 'red.800');
  const alertTextCritical = useColorModeValue('red.800', 'red.200');

  const alertBgWarning = useColorModeValue('orange.50', 'rgba(221, 107, 32, 0.15)');
  const alertBorderWarning = useColorModeValue('orange.200', 'orange.800');
  const alertTextWarning = useColorModeValue('orange.800', 'orange.200');
  // --- UPDATE
  const driverBg = useColorModeValue('white', 'whiteAlpha.50');
  // --- END UPDATE

  // ==================== MEMOIZED CALCULATIONS ====================
  const { status, risk, confidence, rul, statusColor, StatusIcon } = useMemo(() => {
    if (!assetHealth) return {};

    const rawStatus = assetHealth.health_status?.toLowerCase() || 'unknown';
    const riskVal = assetHealth.failure_probability ? Math.round(assetHealth.failure_probability * 100) : 0;
    const confVal = assetHealth.confidence_score ? Math.round(assetHealth.confidence_score * 100) : null;
    const rulVal = assetHealth.predicted_rul_days ?? 'N/A';
    // --- UPDATE: 0–1 or 0–100 scales; null when the model sent nothing
    const p = failureProbability(assetHealth);
    const riskFixed = p === null ? null : Math.round(p * 100);
    const cRaw = Number(assetHealth.confidence_score ?? assetHealth.confidence);
    const confFixed = Number.isFinite(cRaw) ? Math.round(cRaw > 1 ? cRaw : cRaw * 100) : null;
    const rulNum = Number(assetHealth.predicted_rul_days);
    const rulFixed = assetHealth.predicted_rul_days === null || assetHealth.predicted_rul_days === undefined || !Number.isFinite(rulNum) ? '—' : Math.round(rulNum * 10) / 10;
    void riskVal; void confVal; void rulVal;
    // --- END UPDATE

    let color = 'gray';
    let icon = FaHeartbeat;
    
    if (rawStatus === 'good' || rawStatus === 'normal' || rawStatus === 'healthy') { // --- UPDATE: + healthy
      color = 'green'; icon = FaCheckCircle;
    } else if (rawStatus === 'warning') {
      color = 'orange'; icon = FaExclamationTriangle;
    } else if (rawStatus === 'critical') {
      color = 'red'; icon = FaExclamationTriangle;
    }

    return { status: rawStatus, risk: riskFixed, confidence: confFixed, rul: rulFixed, statusColor: color, StatusIcon: icon }; // --- UPDATE: fixed scales
  }, [assetHealth]);

  // --- UPDATE: model factors (hook before the early return)
  const factors = useMemo(() => factorsOf(assetHealth), [assetHealth]);
  // --- END UPDATE

  // ==================== EARLY RETURN ====================
  // Must happen AFTER all hooks are called to obey Rules of Hooks
  if (!assetHealth) return null;

  return (
    <MotionBox 
      variants={itemVariants}
      bg={innerBg}
      p={3}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <VStack align="stretch" spacing={3}>
        
        {/* Header */}
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Icon as={FaBrain} color="purple.500" boxSize={4} />
            <Text fontSize="xs" fontWeight="800" color={mutedText} textTransform="uppercase" letterSpacing="wider">
              AI Health Analysis
            </Text>
          </HStack>
          <Badge 
            colorScheme={statusColor} 
            variant="subtle" 
            px={2} py={0.5} 
            borderRadius="full" 
            display="flex" 
            alignItems="center"
            gap={1}
          >
            <Icon as={StatusIcon} boxSize={3} />
            {status === 'unknown' ? 'NOT SCORED' : status.toUpperCase()}{/* --- UPDATE */}
          </Badge>
        </Flex>

        <Divider borderColor={borderColor} />

        {/* Metrics Grid */}
        <SimpleGrid columns={2} spacing={3}>
          <Box bg={cardBg} p={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
            <HStack spacing={1.5} mb={1}>
              <Icon as={FaClock} color="blue.400" boxSize={3} />
              <Text fontSize="2xs" color={mutedText} fontWeight="700" textTransform="uppercase">Predicted RUL</Text>
            </HStack>
            <Text fontSize="md" fontWeight="800" color={textColor}>
              {rul} {rul !== '—' && <Text as="span" fontSize="xs" color={mutedText} fontWeight="600">days</Text>}{/* --- UPDATE */}
            </Text>
          </Box>

          <Box bg={cardBg} p={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
            <HStack spacing={1.5} mb={1}>
              <Icon as={FaBullseye} color="purple.400" boxSize={3} />
              <Text fontSize="2xs" color={mutedText} fontWeight="700" textTransform="uppercase">Confidence</Text>
            </HStack>
            <Text fontSize="md" fontWeight="800" color={textColor}>
              {confidence !== null ? `${confidence}%` : '—'}{/* --- UPDATE */}
            </Text>
          </Box>
        </SimpleGrid>

        {/* Failure Risk Progress Bar */}
        {risk !== null && risk !== undefined && ( /* --- UPDATE: 0 % is a real value */
          <Box bg={cardBg} p={2.5} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
            <Flex justify="space-between" mb={1} align="baseline">
              <Text fontSize="2xs" color={mutedText} fontWeight="700" textTransform="uppercase">
                Calculated Risk
              </Text>
              <Text fontSize="xs" fontWeight="900" color={`${statusColor}.500`}>
                {risk}%
              </Text>
            </Flex>
            <Progress 
              value={risk} 
              size="sm"
              colorScheme={statusColor}
              borderRadius="full"
              hasStripe={risk > 70}
              isAnimated={risk > 70}
              bg={progressBg} // FIX: Now referencing the top-level hook variable
            />
          </Box>
        )}

        {/* --- UPDATE: FlowSense explainable risk, model factors, live vs ML */}
        {liveStatus && liveStatus !== 'unknown' && (
          <HStack spacing={2} fontSize="xs">
            <Text color={mutedText} fontWeight="700">LIVE SENSORS</Text>
            <Badge colorScheme={liveStatus === 'critical' ? 'red' : liveStatus === 'warning' ? 'orange' : 'green'}>{liveStatus}</Badge>
            {((liveStatus === 'critical' && status !== 'critical') || (liveStatus === 'normal' && status === 'critical')) && <Tooltip label="The live readings and the ML model disagree — confirm on site." hasArrow><Badge colorScheme="yellow" variant="outline">differs from ML</Badge></Tooltip>}
          </HStack>
        )}
        {factors.length > 0 && (
          <Box bg={cardBg} p={2.5} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
            <HStack spacing={1.5} mb={1}><Icon as={FaListUl} color="purple.400" boxSize={3} /><Text fontSize="2xs" color={mutedText} fontWeight="700" textTransform="uppercase">Model factors</Text></HStack>
            <Wrap spacing={1}>{factors.map((f) => <WrapItem key={f.label}><Badge variant="subtle" colorScheme="purple" fontSize="2xs">{String(f.label).replace(/_/g, ' ')}{f.value !== null && f.value !== undefined && Number.isFinite(Number(f.value)) ? ` ${Math.round(Number(f.value) * 100) / 100}` : ''}</Badge></WrapItem>)}</Wrap>
          </Box>
        )}
        {riskScore && riskScore.score !== null && riskScore.score !== undefined && (
          <Box bg={driverBg} p={2.5} borderRadius="md" borderWidth="1px" borderColor={borderColor} role="region" aria-label="FlowSense risk explanation">
            <Flex justify="space-between" align="center" mb={1}>
              <HStack spacing={1.5}><Icon as={FaBrain} color="purple.400" boxSize={3} /><Text fontSize="2xs" color={mutedText} fontWeight="700" textTransform="uppercase">FlowSense risk</Text></HStack>
              <Badge colorScheme={riskScore.color} borderRadius="full">{riskScore.score}/100 · {riskScore.label}</Badge>
            </Flex>
            {riskScore.drivers.map((x) => <Text key={x.label} fontSize="xs" color={textColor}>+{x.points} · {x.label}</Text>)}
            {riskScore.actions.length > 0 && (
              <HStack align="start" spacing={1.5} mt={1.5}><Icon as={FaLightbulb} color="yellow.400" boxSize={3} mt={0.5} /><Text fontSize="xs" color={textColor}>{riskScore.actions.join(' ')}</Text></HStack>
            )}
          </Box>
        )}
        {(assetHealth.model_version || assetHealth.last_scored_at || assetHealth.updated_at || assetHealth.prediction_date) && (
          <Text fontSize="2xs" color={mutedText}>{assetHealth.model_version ? `Model ${assetHealth.model_version}` : ''}{(assetHealth.last_scored_at || assetHealth.updated_at || assetHealth.prediction_date) ? ` · scored ${new Date(assetHealth.last_scored_at || assetHealth.updated_at || assetHealth.prediction_date).toLocaleString()}` : ''}</Text>
        )}
        {/* --- END UPDATE */}

        {/* Warning / Critical Alerts */}
        {status !== 'good' && status !== 'normal' && status !== 'unknown' && (
          <Alert 
            status={status === 'critical' ? 'error' : 'warning'} 
            borderRadius="md" 
            py={2} px={3}
            bg={status === 'critical' ? alertBgCritical : alertBgWarning}
            borderWidth="1px"
            borderColor={status === 'critical' ? alertBorderCritical : alertBorderWarning}
          >
            <AlertIcon boxSize={4} />
            <Text fontSize="xs" fontWeight="600" color={status === 'critical' ? alertTextCritical : alertTextWarning}>
              {status === 'critical' 
                ? 'Immediate maintenance is highly recommended to prevent failure.' 
                : 'Asset is showing signs of degradation. Schedule inspection.'}
            </Text>
          </Alert>
        )}

      </VStack>
    </MotionBox>
  );
};

export default AssetHealthPanel;